import React, { useState, useEffect } from "react";
import { Card, CardHeader, Icon } from "@ui5/webcomponents-react";
import { Box } from "@mui/material";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const InvoiceTimelineCard = ({ invoiceData }) => {
  const [timeframe, setTimeframe] = useState("day");
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    processData(invoiceData, timeframe);
  }, [invoiceData, timeframe]);

  const processData = (data, timeframe) => {
    let groupedData = {};
    data.forEach(invoice => {
      const date = new Date(invoice.scan_date);
      let key;
      switch (timeframe) {
        case "day":
          key = new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(date); // Day of the week (e.g., "Mon", "Tue", etc.)
          break;
        case "week":
          const weekNumber = getWeekNumber(date);
          key = `${date.getFullYear()}-W${weekNumber}`; // YYYY-W<week number>
          break;
        case "month":
          key = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(date); // Month name (e.g., "Jan", "Feb", etc.)
          break;
        default:
          key = new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(date);
      }
      if (!groupedData[key]) groupedData[key] = 0;
      groupedData[key]++;
    });

    const formattedData = Object.keys(groupedData).map(label => ({
      name: label,
      value: groupedData[label]
    }));

    // Sort the data array based on the name property
    const sortedData = formattedData.sort((a, b) => {
      if (timeframe === "month") {
        // Sort months chronologically
        const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return monthOrder.indexOf(a.name) - monthOrder.indexOf(b.name);
      } else {
        // Sort weeks chronologically
        const [aYear, aWeek] = a.name.split('-W');
        const [bYear, bWeek] = b.name.split('-W');
        if (aYear !== bYear) {
          return parseInt(aYear) - parseInt(bYear);
        } else {
          return parseInt(aWeek) - parseInt(bWeek);
        }
      }
    });

    setChartData(sortedData);
  };

  const getWeekNumber = (date) => {
    const startDate = new Date(date.getFullYear(), 0, 1);
    const days = Math.floor((date - startDate) / (24 * 60 * 60 * 1000));
    return Math.ceil((days + startDate.getDay() + 1) / 7);
  };

  const handleTimeframeToggle = () => {
    switch (timeframe) {
      case "day":
        setTimeframe("week");
        break;
      case "week":
        setTimeframe("month");
        break;
      case "month":
        setTimeframe("day");
        break;
      default:
        setTimeframe("day");
    }
  };
    const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="label">Scans: {payload[0].value}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card>
      <CardHeader 
        avatar={<Icon name="calendar" />} 
        subtitleText="Click to toggle timeframe" 
        titleText="Volume of Scans by Timeframe" 
        interactive // Make the CardHeader clickable
        onClick={handleTimeframeToggle} // Toggle timeframe on click
        additionalText={`Viewing ${timeframe}`} // Status showing the current timeframe
      />
      <Box sx={{ height: 470, mr: 1, ml: 3, mb: "5px" }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Area type="monotone" dataKey="value" stroke="#046C7A" fill="#046C7A" /> {/* SAP's teal color */}
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Card>
  );
};

export default InvoiceTimelineCard;
